import DeleteMyAccountView from "issara-sdk/apis/DeleteMyAccountView_users";
import FacebookConnect from "issara-sdk/apis/FacebookConnect_users";
import GoogleConnect from "issara-sdk/apis/GoogleConnect_users";
import LineConnect from "issara-sdk/apis/LineConnect_users";
import SocialAccountDisconnectView from "issara-sdk/apis/SocialAccountDisconnectView_users";
import SocialConnectionsView from "issara-sdk/apis/SocialConnectionsView_users";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";
import { clearLoginSession } from "react-lib/apps/HISV3/common/sequence/AppInitialize";
import { getDeviceIdAndUserSubScription } from "react-lib/apps/HISV3/common/sequence/GetUserSubScription";

import CONFIG from "config/config";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = {
  INIT: "INIT",
  DELETE_ACCOUNT: "DELETE_ACCOUNT",
  DISCONNECT: "DISCONNECT",
  FACEBOOK_CONNECT: "FACEBOOK_CONNECT",
  GOOGLE_CONNECT: "GOOGLE_CONNECT",
  LINE_CONNECT: "LINE_CONNECT",
} as const;

export const SEQUENCE_NAME = "AccountInformation" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  AccountInformationSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    connectionAvailable: ConnectionAvailableType[];
    connectionItems: ConnectionItemType[];
  }> | null;
}>;

export type PickedState = PickMainState<
  "AppInitializeSequence" | "buttonLoadCheck" | "errorMessage" | "userProfile"
>;

export type ConnectionItemType = {
  id: number;
  date_joined: string;
  last_login: string;
  name: string;
  provider: ConnectionProviderType;
};

type ConnectionAvailableType = {
  name: string;
  provider: ConnectionProviderType;
};

export type ConnectionProviderType = "apple" | "facebook" | "google" | "line";

export type ConnectionActionType = "connect" | "default" | "disconnect";

export type MasterOptions = MasterMapOptions<typeof Masters>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.DELETE_ACCOUNT]: {
    onSuccess?: () => void;
  };
  [ACTIONS.DISCONNECT]: { id: number; onSuccess?: () => void };
  [ACTIONS.FACEBOOK_CONNECT]: {
    data: Record<string, any>;
    deviceId: string;
    fbResponse: Record<string, any>;
  };
  [ACTIONS.GOOGLE_CONNECT]: {
    credential: string;
  };
  [ACTIONS.LINE_CONNECT]: {
    access_token: string;
    id_token: string;
  };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  AccountInformationSequence: null,
};

export const DataInitial: Data = {};

const Masters = [["positionAll", {}]] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    {
      AccountInformationSequence: {
        ...state.AccountInformationSequence,
        sequenceIndex: "Action",
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.INIT });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.INIT]: HandleInit,
    [ACTIONS.DELETE_ACCOUNT]: HandleDeleteAccount,
    [ACTIONS.DISCONNECT]: HandleDisconnect,
    [ACTIONS.FACEBOOK_CONNECT]: HandleFacebookConnect,
    [ACTIONS.GOOGLE_CONNECT]: HandleGoogleConnect,
    [ACTIONS.LINE_CONNECT]: HandleLineConnect,
  };

  const { action } = params;

  const handler = actionHandlers[action] as
    | Handler<Params<typeof action>, Promise<void>>
    | undefined;

  if (handler) {
    await handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleInit: Handler<Params<Actions["INIT"]>, Promise<void>> = async (controller, params) => {
  console.log(params);

  const [result] = await SocialConnectionsView.list({
    apiToken: controller.apiToken,
  });

  const state = controller.getState();

  const available: ConnectionAvailableType[] = result?.available ?? [];
  const filteredAvailable = available.filter((item) => item.provider !== "apple");

  controller.setState({
    AccountInformationSequence: {
      ...state.AccountInformationSequence,
      connectionAvailable: filteredAvailable,
      connectionItems: result?.items ?? [],
    },
  });
};

const HandleFacebookConnect: Handler<Params<Actions["FACEBOOK_CONNECT"]>> = async (
  controller,
  params
) => {
  const { deviceId, fbResponse } = params;

  const data = {
    access_token: fbResponse.authResponse?.accessToken,
    app: CONFIG.APP_NAME,
    device_id: deviceId,
    device_type: "webpush",
    unique_id: deviceId,
  };

  const [, error] = await FacebookConnect.post({ apiToken: controller.apiToken, data });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  await HandleInit(controller, { ...params, action: ACTIONS.INIT });
};

const HandleGoogleConnect: Handler<Params<Actions["GOOGLE_CONNECT"]>> = async (
  controller,
  params
) => {
  const { credential } = params;

  const [deviceId, subscription] = await getDeviceIdAndUserSubScription();

  const data = {
    app: CONFIG.APP_NAME,
    device_id: deviceId,
    device_token: JSON.stringify(subscription),
    device_type: "webpush",
    id_token: credential,
    unique_id: deviceId,
  };

  const [, error] = await GoogleConnect.post({ apiToken: controller.apiToken, data });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  await HandleInit(controller, { ...params, action: ACTIONS.INIT });
};

const HandleLineConnect: Handler<Params<Actions["LINE_CONNECT"]>> = async (controller, params) => {
  const [deviceId, subscription] = await getDeviceIdAndUserSubScription();

  const data = {
    access_token: params.access_token,
    app: CONFIG.APP_NAME,
    device_id: deviceId,
    device_token: JSON.stringify(subscription),
    device_type: "webpush",
    id_token: params.id_token,
    unique_id: deviceId,
  };

  const [, error] = await LineConnect.post({ apiToken: controller.apiToken, data });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  await HandleInit(controller, { ...params, action: ACTIONS.INIT });
};

const HandleDeleteAccount: Handler<Params<Actions["DELETE_ACCOUNT"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE_ACCOUNT]: BLC_STATUS.LOADING },
  });

  const [, error] = await DeleteMyAccountView.post({
    apiToken: controller.apiToken,
    data: { user_id: state.userProfile?.id, username: state.userProfile?.username },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnError: null, error });

    return;
  }

  clearLoginSession(controller as any, {});

  controller.setState({
    AppInitializeSequence: {
      ...state.AppInitializeSequence,
      sequenceIndex: "Login",
      password: "",
      username: "",
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE_ACCOUNT]: null },
  });

  params.onSuccess?.();
};

const HandleDisconnect: Handler<Params<Actions["DISCONNECT"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DISCONNECT]: BLC_STATUS.LOADING },
  });

  const [, error] = await SocialAccountDisconnectView.post({
    apiToken: controller.apiToken,
    pk: params.id,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnError: null, error });

    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DISCONNECT]: null },
  });

  await HandleInit(controller, { ...params, action: ACTIONS.INIT });

  params.onSuccess?.();
};
